import {connect} from 'pwa-helpers';
import {LitElement, html, css} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {when} from 'lit/directives/when.js';
import {classMap} from 'lit/directives/class-map.js';
import {msg} from '@lit/localize';
import store from '../store';
import {selectCurrentSection, selectFamilyConfig, selectSectionConfig} from '../selectors';

@customElement('configuration-sections')
export class ConfigurationSections extends connect(store)(LitElement) {

	static styles = [
		css`
			:host {
				background: transparent;
				display: flex;
				flex-direction: column;
				height: 100%;
			}

			.content {
				height: 100%;
				position: relative;
			}
			.content.dashboard {
				overflow: auto;
			}

			.dashboard-layer {
				width: 100%;
				position: absolute;
				left: 0;
				top: 0;
			}
			.dashboard-layer.embed {
				position: relative;
			}

			nav.tabs {
				z-index: 10;
			}

			.section-layer {
				height: 60%;
				width: 100%;
				position: absolute;
				left: 0;
				top: 40%;
				display: block;
				pointer-events: none;
			}

			.section-layer.active {
				display: block;
			}

			.lightProfile .section-layer {
				top: 0;
			}

			.visual-layer {
				height: 40%;
				width: 100%;
				display: flex;
				justify-content: space-around;
				box-sizing: border-box;
			}
			.specsheet .visual-layer {
				height: 100%;
				width: 100%;
			}
			.finalize .visual-layer {
				margin-left: 0;
				width: 100%;
				height: 100%;
			}

			@media (min-width: 768px) {
				.visual-layer {
					position: fixed;
					top: 0;
					height: 100%;
					width: calc(100% - var(--features-column-width) - var(--large-padding));
					margin-left: calc(var(--features-column-width) + var(--large-padding));
					display: flex;
				}

				.lightProfile .visual-layer {
					margin-left: 0;
					width: 100%;
				}
				.solarRecommendations .visual-layer {
					margin-left: 0;
					width: 100%;
				}
				.specsheet .visual-layer {
					margin-left: 0;
					width: 100%;
				}
			}

			@media (min-width: 1024px) {
				.olivio3d .visual-layer {
					margin-left: 0;
					width: 100%;
				}
			}

			.solarRecommendations .visual-layer {
				height: 100%;
			}
			.lightProfileSummary .visual-layer {
				margin-left: 0;
				width: 100%;
				overflow-y: scroll;
			}


			.lightProfileLuminary .section-layer {
				min-height: 100%;
				width: 100%;
				position: absolute;
				left: 0;
				top: 40%;
				display: block;
				pointer-events: none;
			}

			.content.lightProfileLuminary {
				overflow-y: scroll;
				margin-bottom: var(--large-padding);
			}

			.fixed-section {
				position: absolute;
				bottom: 0%;
				width: 100%;
			}

			.spacing-wrapper {
				padding-left: 0;
			}

			@media (min-width: 768px) {
				.section-layer {
					position: absolute;
					top: 0;
					height: 100%;
					display: block;
				}

				.content.lightProfileLuminary {
					overflow-y: scroll;
					margin-bottom: 0;
				}

				.content.lightProfileLuminary::-webkit-scrollbar {
					display: none;
				}

				.lightProfileLuminary .section-layer {
					top: 0;
				}

				.fixed-section {
					position: fixed;
					bottom: 0;
					width: 100%;
				}

				.spacing-wrapper {
					width: 75%;
				}
			}

			.info {
				font: var(--font-widget-big-title);
				color: var(--color-widget-text);
				font-size: 1.25rem;
				line-height: 1.4;
				margin: 100px auto;
				padding: 0 20px;
				text-align: center;
				max-width: 640px;
			}

			@media (min-width: 768px) {
				.info {
					font-size: 1.5rem;
				}
			}
		`
	];

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	stateChanged(state) {
		this.familyConfig = selectFamilyConfig(state);
		this.maintenance = this.familyConfig.maintenance ?? false;
		this.sectionConfig = selectSectionConfig(state);
		this.currentSection = selectCurrentSection(state);
		this.dashboardActive = state.status.dashboardActive;
		this.visualActive = state.status.visualActive;
		this.loggedIn = state.userProfile.data.loggedIn;
		this.family = state.status.family;
		this.embed = state.status.embed;
		this.isConfigurator = this.familyConfig.isConfigurator;

	}

	render() {

		if (!this.sectionConfig) return false;
		const section = this.sectionConfig
			.find(s => s.id === this.currentSection);

		let visualTemplate = null;
		let contentClass = '';

		switch (section.visual) {
			case 'housing':
				visualTemplate = html`
					<visual-housing></visual-housing>`;
				break;
			case 'lightengine':
				visualTemplate = html`
					<visual-lightengine></visual-lightengine>`;
				break;
			case 'optics':
				visualTemplate = html`
					<visual-optics></visual-optics>`;
				break;
			case 'lightdistribution':
				visualTemplate = html`
					<visual-lightdistribution></visual-lightdistribution>`;
				break;
			case 'lightprofile':
				visualTemplate = html`
					<span class="spacing-wrapper">
					<visual-lightprofile></visual-lightprofile>
					</span>`;

				break;
			case 'lightprofilelocation':
				visualTemplate = html`
					<visual-lightprofile-location></visual-lightprofile-location>`;
				break;
			case 'lightprofilesummary':
				visualTemplate = html`
					<visual-lightprofile-summary></visual-lightprofile-summary>`;
				break;
			case 'lightprofileluminary':
				visualTemplate = html`
					<span class="fixed-section">
					<visual-lightprofile-luminary></visual-lightprofile-luminary>
					</span>`;
				break;
			case 'lightprofilebase':
				visualTemplate = html`
					<visual-lightprofile-base></visual-lightprofile-base>`;
				break;
			case 'lightprofilezone':
				visualTemplate = html`
					<visual-lightprofile-zone></visual-lightprofile-zone>`;
				break;
			case 'model':
				visualTemplate = html`
					<visual-model></visual-model>`;
				break;
			case 'solarRecommendations':
				visualTemplate = html`
					<solar-recommendations></solar-recommendations>`;
				break;
			case 'solarLocation':
				visualTemplate = html`
					<visual-solar-location></visual-solar-location>`;
				break;
			case 'solarLuminarCount':
				visualTemplate = html`
					<visual-solar-luminar-count></visual-solar-luminar-count>`;
				break;
			case 'solarLuminar':
				visualTemplate = html`
					<visual-solar-luminar></visual-solar-luminar>`;
				break;
			case 'solarLightTemperature':
				visualTemplate = html`
					<visual-solar-light-temperature></visual-solar-light-temperature>`;
				break;
			case 'solarActivityLevel':
				visualTemplate = html`
					<visual-solar-activity-level></visual-solar-activity-level>`;
				break;
			case 'solarProfile':
				visualTemplate = html`
					<visual-solar-profile></visual-solar-profile>`;
				break;
			case 'solarStart':
				visualTemplate = html`
					<visual-solar-start></visual-solar-start>`;
				break;
			case 'olivio3d':
				visualTemplate = html`
					<visual-olivio-3d></visual-olivio-3d>`;
				break;
			case 'lightProfileSummary':
				visualTemplate = html`
					<visual-lightprofile-summary></visual-lightprofile-summary>`;
				break;
			case 'specsheet':
				visualTemplate = html`
					<visual-specsheet></visual-specsheet>`;
				break;
			case 'finalize':
				visualTemplate = html`
					<visual-finalize></visual-finalize>`;
				break;
			case 'test':
				visualTemplate = html`
					<visual-test></visual-test>`;
				break;
			default:
				visualTemplate = html`
					<visual-placeholder .section=${section}></visual-placeholder>`;
				break;
		}

		if (section.visual !== null && section.visual.indexOf('lightprofile') !== -1) {
			contentClass = 'lightProfile';
		} else {
			contentClass = 'lineProfile';
		}

		if (section.visual === 'lightprofile') {
			contentClass = 'lightProfileInverted';
		}

		if (section.visual !== null && section.visual.indexOf('luminary') !== -1) {
			contentClass = 'lightProfileLuminary';
		}
		const dashboardClass = this.dashboardActive ? 'dashboard' : '';


		return html`
			<div class="content ${contentClass} ${section.visual} ${section.theme} ${dashboardClass}">
				${when((this.maintenance && !this.loggedIn),
					() => html`
						<div class="info">
							<h2>${msg('Configurator temporarily unavailable.')}</h2>
							<p>${msg('This Configurator is currently being updated and is therefore not accessible at the moment. For assistance, please contact your Selux representative.')}</p>
							<p>${msg('Thank you for your understanding')}</p>
						</div>
					`,
					() => html`
						${when((this.family === 'Start' && !this.loggedIn),
							() => html`
								<div class="info">
									${msg('Please login.')}
								</div>
							`,
							() => html`
								${when((this.isConfigurator && this.dashboardActive) || this.embed,
									() => html`
										<div class="dashboard-layer ${classMap({embed: this.embed})}">
											${when(section.useAttributesView, () => html`
												<attributes-view></attributes-view>
											`, () => html`
												<dashboard-view></dashboard-view>
											`)}
										</div>`,
									() => html`
										<div class="visual-layer ${classMap({active: this.visualActive})}">
											${visualTemplate}
										</div>
										${when((section.features && section.features.length > 0) || (section.widgets && section.widgets.length > 0),
											() => html`
												<div class="section-layer  ${classMap({active: !this.visualActive})}">
													<configuration-section .section=${section}></configuration-section>
												</div>`,
											() => html``)
										}
									`)
								}
							`)
						}
					`)}

			</div>
		`;
	}
}
